/**
 * Sessione utente: unico punto che legge/scrive token e identità nello store
 * (Pinia + localStorage via useStorage). Usato da API.js (refresh), authService
 * (login) e httpErrors (logout forzato). Nessuna chiamata HTTP qui dentro.
 */
import { isLoggedStore } from '../store/logged';

/** Ruoli tenant accettati dal gestionale partner (F1: adminEmployee -> admin). */
export const ROLES = Object.freeze({
  EMPLOYEE: 'employee',
  ADMIN: 'admin',
  ADMIN_LEGACY: 'adminEmployee', // token residui pre-migrazione
  SALESVENDOR: 'salesvendor',
});

export const isPartnerAdmin = (role) => role === ROLES.ADMIN || role === ROLES.ADMIN_LEGACY;

/** Decodifica il payload di un JWT (senza verificarlo: la verifica è del server). */
export function parseJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch {
    return null;
  }
}

/** Salva access/refresh token e le relative scadenze (ms). */
export function applyTokens({ access_token, refreshtoken }) {
  const store = isLoggedStore();
  store.accessToken = access_token;
  store.refreshToken = refreshtoken;
  store.jwtExp = (parseJwt(access_token)?.exp ?? 0) * 1000;
  store.refreshExp = (parseJwt(refreshtoken)?.exp ?? 0) * 1000;
  return parseJwt(access_token);
}

export const getAccessToken = () => isLoggedStore().accessToken || '';
export const getRefreshToken = () => isLoggedStore().refreshToken || '';

/** true se il token (access o refresh) risulta scaduto rispetto all'orologio locale. */
export function isExpired(expMs, skewMs = 5000) {
  const exp = Number(expMs);
  return !exp || Date.now() >= exp - skewMs;
}
export const isAccessTokenExpired = () => isExpired(isLoggedStore().jwtExp);
export const isRefreshTokenExpired = () => isExpired(isLoggedStore().refreshExp);

/** Azzera la sessione locale (token, identità, flag partner). */
export function clearSession() {
  const store = isLoggedStore();
  store.isLogged = false;
  store.viewMenu = false;
  store.accessToken = '';
  store.refreshToken = '';
  store.jwtExp = '';
  store.refreshExp = '';
  store.id = '';
  store.mail = '';
  store.role = '';
  store.employee = {};
  store.partner = {};
  store.bookingType = false;
  store.journeyType = false;
  store.restaurantType = false;
}

let redirectingToLogin = false;

/** Logout forzato: pulizia sessione + redirect al login (una sola volta anche con N chiamate fallite). */
export async function forceLogout() {
  clearSession();
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  try {
    // import dinamico: evita il ciclo services -> router -> views -> services
    const { default: router } = await import('../router');
    if (router.currentRoute.value.name !== 'Login') await router.push('/login');
  } finally {
    setTimeout(() => { redirectingToLogin = false; }, 1000);
  }
}
